import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, BellRing, FileArchive, Lock, Send } from "lucide-react";
import { useApi, useFetch, lastMonth, errMsg } from "../lib/api";
import { useMember } from "../lib/member";
import { Button } from "./ui/button";
import { MonthPicker } from "./DateField";

const outline = "border-[#1F2A3E] hover:bg-[#1A2234] hover:text-white";

export const ContractorExportBar = ({ eid }) => {
  const api = useApi();
  const { me } = useMember();
  const [month, setMonth] = useState(lastMonth());
  const [busy, setBusy] = useState(false);
  const [withWorkers, setWithWorkers] = useState(true);
  const { data: rules, reload } = useFetch(`/pe/${eid}/rules`);
  const save = async (path, params, fallback) => {
    try {
      const r = await api.get(path, { params, responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || fallback;
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch { toast.error("Download failed"); }
  };
  const exportXlsx = () => save(`/pe/${eid}/contractors.xlsx`, { month }, `Contractors_${month}.xlsx`);
  const exportZip = async () => { setBusy(true); await save(`/pe/${eid}/profiles.zip`, { month, workers: withWorkers }, `Contractor_Profiles_${month}.zip`); setBusy(false); };
  const setRule = async (v) => {
    try { await api.put(`/pe/${eid}/mismatch-hold`, { auto_hold_rule: v }); reload(); toast.success("Deadline hold rule saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const packNow = async () => {
    if (!window.confirm("Email last month's audit pack (all contractor profile PDFs) to this principal employer's approvers now?")) return;
    setBusy(true);
    try {
      const p = (await api.post("/pe/audit-packs/send", null, { params: { eid } })).data.packs[0];
      p?.sent ? toast.success(`Audit pack emailed to ${p.sent} approver(s)`) : toast.error(p?.why || "Nothing sent");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const holdNow = async () => {
    if (!window.confirm("Put payment on hold now for this principal employer's contractors whose challan for last month is still missing?")) return;
    setBusy(true);
    try { const r = (await api.post("/pe/deadline-holds/run", null, { params: { eid } })).data; toast.success(r.placed.length ? `${r.placed.length} payment hold(s) placed for ${r.month}` : `No new holds for ${r.month}`); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const remind = async () => {
    if (!window.confirm("Email this principal employer's contractors whose PF or ESIC challan for last month is still missing (copy to the compliance inbox)?")) return;
    setBusy(true);
    try {
      const r = (await api.post("/pe/filing-reminders/run", null, { params: { eid } })).data;
      const extra = r.already_today ? ` (${r.already_today} already reminded today)` : "";
      toast.success(r.reminded.length ? `Reminded ${r.reminded.length} contractor(s) for ${r.month}${extra}` : `No new reminders for ${r.month}${extra}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="c-surface p-4 mb-5 flex flex-wrap items-end gap-3" data-testid="pe-export-bar">
      <div className="text-xs text-slate-400 max-w-md mr-auto">Download all contractors' workers and one month of wages as one Excel file (Workers, Wages and Summary sheets). Contractors missing last month's PF/ESIC challan are reminded on the 10th and 14th, and can be put on hold on the 16th.</div>
      <label className="grid gap-1"><span className="text-xs text-slate-400">Wage month</span>
        <MonthPicker value={month} onChange={setMonth} testid="pe-export-month" className="w-44" /></label>
      <Button variant="outline" className={outline} onClick={exportXlsx} data-testid="pe-export-btn"><FileSpreadsheet size={14} className="mr-1.5" />Export workers & wages</Button>
      {me?.role === "admin" && <Button variant="outline" className={outline} onClick={remind} disabled={busy} data-testid="pe-remind-btn"><BellRing size={14} className="mr-1.5" />Remind these contractors now</Button>}
      <div className="basis-full flex flex-wrap items-end gap-3 border-t border-[#1F2A3E] pt-3">
        <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer"><input type="checkbox" className="accent-[#00E599]" checked={withWorkers} onChange={(e) => setWithWorkers(e.target.checked)} data-testid="pe-zip-workers" />Include full worker lists</label>
        <Button variant="outline" className={outline} onClick={exportZip} disabled={busy} data-testid="pe-zip-btn"><FileArchive size={14} className="mr-1.5" />Download all profile PDFs (zip)</Button>
        {me?.role === "admin" && <Button variant="outline" className={outline} onClick={packNow} disabled={busy} title="Approvers also get this automatically on the 20th" data-testid="pe-audit-pack-btn"><Send size={14} className="mr-1.5" />Email audit pack now</Button>}
        <label className="grid gap-1 md:ml-auto w-full md:w-auto"><span className="text-xs text-slate-400 flex items-center gap-1"><Lock size={11} />Hold payment on the 16th if a challan is still missing</span>
          <select className="c-input w-full md:!w-80" value={rules?.auto_hold_rule || "reminded"} onChange={(e) => setRule(e.target.value)} disabled={!["admin", "staff"].includes(me?.role)} data-testid="pe-auto-hold-rule">
            <option value="reminded">Only after both reminders (10th & 14th)</option>
            <option value="any">Any missing challan, reminded or not</option>
            <option value="off">Never — alerts only</option>
          </select></label>
        {me?.role === "admin" && <Button variant="outline" className={outline} onClick={holdNow} disabled={busy} data-testid="pe-deadline-hold-btn"><Lock size={14} className="mr-1.5" />Apply deadline holds now</Button>}
      </div>
    </div>);
};
